<script setup lang="ts">
import type { PropType } from "vue"
import type { IRadioInputItem } from "@/types"
import { useField } from "vee-validate"
import { toRef } from "vue"

const props = defineProps({
  id: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: "",
  },
  items: {
    type: Array as PropType<IRadioInputItem[]>,
    default: () => [],
  },
  modelValue: {
    type: String,
    default: "",
  },
  error: {
    type: String,
    default: "",
  },
  orientation: {
    type: String as PropType<"vertical" | "horizontal">,
    default: "horizontal",
  },
})

const emit = defineEmits(["update:modelValue"])

const name = toRef(props, "name")

const { value: field, errorMessage, handleChange } = useField(name)

function onChange(event: Event) {
  const target = event.target as HTMLInputElement
  handleChange(target.value)
  emit("update:modelValue", target.value)
}
</script>

<template>
  <fieldset class="gpx-fieldset">
    <slot name="label">
      <legend class="text-sm ml-1 mb-1 text-gray-700">
        {{ label }}
      </legend>
    </slot>
    <div
      class="flex gap-2 flex-col"
      :class="{
        'flex-col md:flex-row md:gap-4': orientation === 'horizontal',
      }"
    >
      <div v-for="(item, index) in items" :key="index">
        <label :for="item.value" class="gpx-radio-input-label">
          <input
            :id="item.value"
            :name="item.value"
            :value="item.value"
            type="radio"
            :checked="modelValue === item.value || field === item.value"
            :disabled="item.disabled"
            class="accent-primary"
            :class="{ 'text-gray-500 cursor-not-allowed': item.disabled }"
            @change="onChange"
            @click="onChange"
            @keyup.space="onChange"
            @keyup.enter="onChange"
          >
          <span :class="{ 'gpx-radio-input-item_disabled': item.disabled }">{{ item.label }}</span>
        </label>
      </div>
    </div>
    <span v-if="error || errorMessage" class="gpx-field-error">
      {{ error || errorMessage }}
    </span>
  </fieldset>
</template>

<style scoped>
.gpx-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
}

.gpx-radio-input-label {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}

.gpx-field-error {
  margin-left: 0.3rem;
}
</style>
